import { useEffect, useState } from 'react'

import { cx } from '@/utils/strings'
import { Asset } from 'contentful'
import { getLocale, locales, defaultLocale } from '@/utils/locale'

export type EventProps = {
  title: string
  startTime?: string
  isCustomTime?: boolean
  customTime?: string
  category?: string
  speakers?: string[]
  date: string
  timeZone?: string
  image?: Asset<'WITH_ALL_LOCALES'>
}

const partsToTimeString = (parts: Intl.DateTimeFormatPart[]) =>
  parts
    .slice(0, -2)
    .map((item) => item.value)
    .join('')

// Format start and end time to h:MM - h:MMa/p
const formatTime = (startTime: string, date: string, timeZone?: string) => {
  if (!startTime || !date) return ''
  if (!timeZone) timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  try {
    const startDate = new Date(date.replace(/T\d\d:\d\d/, `T${startTime}`))
    const formatter = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    })
    const startParts = formatter.formatToParts(startDate)
    const start = partsToTimeString(startParts)
    const amPm = startParts?.pop()?.value.toLowerCase()
    return `${start}${amPm}`
  } catch {
    return ''
  }
}

const categoryColors: Record<string, string> = {
  keynote: `bg-primary-300 dark:bg-deepBlue`,
  presentation: `bg-accent-gold dark:bg-[#AC5800]`,
  fireside: `bg-[#FF97A3] dark:bg-[#ac5800]`,
  panel: `bg-[#5BE5EE] dark:bg-[#01858E]`,
  'case study': `bg-[#C57FFF] dark:bg-[#7E1DCB]`,
}

export default function Event({
  title,
  startTime,
  isCustomTime = false,
  customTime,
  category,
  speakers: _speakers,
  date,
  timeZone,
  image
}: EventProps) {
  const locale = getLocale()
  const contentfulLocale = locales.find((loc) => loc.slug === locale)?.contentfulCode || defaultLocale

  // Have to use state + effect because of SSR hydration issues with datetime
  const [time, setTime] = useState('')

  useEffect(() => {
    if (!startTime && !isCustomTime) return
    setTime(isCustomTime ? customTime || '' : formatTime(startTime || '', date, timeZone))
  }, [startTime, isCustomTime, customTime, timeZone])

  const speakers = _speakers?.filter((speaker) => speaker)

  return (
    <div className="grid gap-3 grid-cols-[131px_1fr] items-start rounded-lg bg-white dark:bg-secondary-800 p-3 border-b border-secondary-100">
      <div className="border-r border-secondary-100 h-full">
        <div className="font-heading text-sm mb-1">{time}</div>
        {category && (
          <div
            className={cx(
              `text-xs text-center rounded-sm p-1 w-auto inline-block dark:text-secondary-50`,
              categoryColors[category.toLowerCase()] || `bg-secondary-200`
            )}
          >
            {category}
          </div>
        )}
        {image && (
          <div className="py-4 pr-4">
            <img
              src={image.fields.file?.[contentfulLocale]?.url || ''}
              alt={image.fields.title?.[contentfulLocale] || ''}
              className="w-full h-auto rounded-lg"
            />
          </div>
        )}
      </div>
      <div>
        <h4 className={cx(`text-xl`, (speakers?.length ?? 0) > 0 ? `mb-3` : `mb-0`)}>{title}</h4>
        {speakers?.map((speaker) => (
          <p key={speaker} className="text-base mb-0">
            {speaker}
          </p>
        ))}
      </div>
    </div>
  )
}
